import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useCommiteeIdFromUrl } from '@/utils/router';
import { apiGetBlobWithWarning } from './api.utils';
import { extractErrorMessage } from './mutations.utils';

// Downloads call `mutate`, which swallows the rejection: without this the button just stops
// spinning, and neither the user nor Sentry hears about the failure.
export function reportDownloadError(error: unknown) {
  toast.error(`L'export a échoué : ${extractErrorMessage(error)}`);
  Sentry.captureException(error);
}

interface DownloadOptions {
  url: string;
  filename: string;
}

interface DownloadResult<Params extends object | undefined> {
  download: (params: Params) => void;
  isPending: boolean;
}

export function downloadBlob<Params extends object | undefined>(
  options: DownloadOptions
): DownloadResult<Params> {
  const { url, filename } = options;

  const mutation = useMutation({
    mutationFn: (params: Params) => apiGetBlobWithWarning(url, params),
    onSuccess: ({ blob, warning }) => {
      downloadFromBlob(blob, filename);
      if (warning) toast(warning, { icon: '⚠️', duration: 15_000 });
    },
    onError: reportDownloadError,
  });

  return {
    download: mutation.mutate,
    isPending: mutation.isPending,
  };
}

export function downloadCommiteeBlob<Params extends object | undefined>(
  options: DownloadOptions
): DownloadResult<Params> {
  const commiteeId = useCommiteeIdFromUrl();
  const computedUrl = useMemo(() => {
    if (options.url.includes(':commiteeId')) {
      if (!commiteeId) throw new Error('Commitee ID is required');
      return options.url.replace(':commiteeId', commiteeId.toString());
    }
    return options.url;
  }, [options.url, commiteeId]);

  return downloadBlob<Params>({
    url: computedUrl,
    filename: options.filename,
  });
}

export function downloadFromBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadCommiteeCsv(options: DownloadOptions): { download: () => void } {
  const { download } = downloadCommiteeBlob<undefined>(options);
  return { download: () => download(undefined) };
}
